import { Link2 } from "lucide-react";
import { t } from "i18next";
import { useState } from "react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isSafeUrl } from "@/lib/utils/urlUtils";

interface FormattingToolbarProps {
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isCode: boolean;
  isBlockquote: boolean;
  isHeading1: boolean;
  isHeading2: boolean;
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onApplyLink: (url: string) => void;
  onRemoveLink: () => void;
  onBeforeLinkEdit?: () => void;
  onToggleCode: () => void;
  onToggleBlockquote: () => void;
  onToggleHeading1: () => void;
  onToggleHeading2: () => void;
  onAttachClick?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showAttachButton?: boolean;
  emojiPicker?: React.ReactNode;
  onLinkPopoverOpenChange?: (open: boolean) => void;
}

const ToolbarTooltip: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const TOOLBAR_BUTTON_CLASS = "size-8 p-0 text-sm leading-none";

export const FormattingToolbar: React.FC<FormattingToolbarProps> = ({
  isBold,
  isItalic,
  isLink,
  isHeading1,
  isHeading2,
  isBlockquote,
  onToggleBold,
  onToggleItalic,
  onApplyLink,
  onRemoveLink,
  onBeforeLinkEdit,
  onToggleHeading1,
  onToggleHeading2,
  onToggleBlockquote,
  onAttachClick,
  fileInputRef,
  onFileChange,
  showAttachButton = true,
  emojiPicker,
  onLinkPopoverOpenChange,
}) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleLinkPopoverOpenChange = (open: boolean) => {
    setLinkPopoverOpen(open);
    onLinkPopoverOpenChange?.(open);
  };

  const normalizeUrl = (raw: string): string => {
    const value = raw.trim();
    if (!value) return "";
    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value);
    return hasProtocol ? value : `https://${value}`;
  };

  const handleApplyLink = () => {
    const normalized = normalizeUrl(linkValue);
    if (!isSafeUrl(normalized)) {
      setLinkError(t("common.invalidUrl"));
      return;
    }

    onApplyLink(normalized);
    handleLinkPopoverOpenChange(false);
    setLinkValue("");
    setLinkError(null);
  };

  const closeLinkPopover = () => {
    handleLinkPopoverOpenChange(false);
    setLinkValue("");
    setLinkError(null);
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex gap-2">
        {/* Bold Button */}
        <ToolbarTooltip label={t("common.bold")}>
          <Button
            variant={isBold ? "formattingActive" : "formattingInactive"}
            size="formatting"
            className={`${TOOLBAR_BUTTON_CLASS} font-bold`}
            onClick={onToggleBold}
            type="button"
            aria-label={t("common.bold")}
          >
            B
          </Button>
        </ToolbarTooltip>

        {/* Italic Button */}
        <ToolbarTooltip label={t("common.italic")}>
          <Button
            variant={isItalic ? "formattingActive" : "formattingInactive"}
            size="formatting"
            className={`${TOOLBAR_BUTTON_CLASS} italic`}
            onClick={onToggleItalic}
            type="button"
            aria-label={t("common.italic")}
          >
            I
          </Button>
        </ToolbarTooltip>

        {/* Link Button */}
        {isLink ? (
          <ToolbarTooltip label={t("common.remove")}>
            <Button
              variant="formattingActive"
              size="formatting"
              className={TOOLBAR_BUTTON_CLASS}
              onClick={onRemoveLink}
              type="button"
              aria-label={t("common.remove")}
              onMouseDown={(e) => {
                e.preventDefault();
                onBeforeLinkEdit?.();
              }}
            >
              <Link2 className="size-4" />
            </Button>
          </ToolbarTooltip>
        ) : (
          <Popover
            open={linkPopoverOpen}
            onOpenChange={(open) => {
              handleLinkPopoverOpenChange(open);
              if (!open) {
                setLinkValue("");
                setLinkError(null);
              }
            }}
          >
            <ToolbarTooltip label={t("common.addLink")}>
              <PopoverTrigger asChild>
                <Button
                  variant="formattingInactive"
                  size="formatting"
                  className={TOOLBAR_BUTTON_CLASS}
                  type="button"
                  aria-label={t("common.addLink")}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onBeforeLinkEdit?.();
                  }}
                >
                  <Link2 className="size-4" />
                </Button>
              </PopoverTrigger>
            </ToolbarTooltip>
            <PopoverContent className="w-72 space-y-2 p-3" align="start">
              <Input
                value={linkValue}
                onChange={(e) => {
                  setLinkValue(e.target.value);
                  if (linkError) {
                    setLinkError(null);
                  }
                }}
                placeholder="https://example.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyLink();
                  }
                }}
              />
              {linkError && <p className="text-xs text-red-500">{linkError}</p>}
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={closeLinkPopover}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="submit"
                  size="sm"
                  type="button"
                  onClick={handleApplyLink}
                >
                  {t("common.apply")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Large Heading Button */}
        <ToolbarTooltip label={t("common.headingLarge")}>
          <Button
            variant={isHeading1 ? "formattingActive" : "formattingInactive"}
            size="formatting"
            className={TOOLBAR_BUTTON_CLASS}
            onClick={onToggleHeading1}
            type="button"
            aria-label={t("common.headingLarge")}
          >
            H1
          </Button>
        </ToolbarTooltip>

        {/* Small Heading Button */}
        <ToolbarTooltip label={t("common.headingSmall")}>
          <Button
            variant={isHeading2 ? "formattingActive" : "formattingInactive"}
            size="formatting"
            className={TOOLBAR_BUTTON_CLASS}
            onClick={onToggleHeading2}
            type="button"
            aria-label={t("common.headingSmall")}
          >
            H2
          </Button>
        </ToolbarTooltip>

        {/* Code Button */}
        {/* <Button
        variant={isCode ? "formattingActive" : "formattingInactive"}
        size="formatting"
        onClick={onToggleCode}
        type="button"
        aria-label={t("common.code")}
      >
        &lt;&gt;
      </Button> */}

        {/* Blockquote Button */}
        <ToolbarTooltip label={t("common.blockquote")}>
          <Button
            variant={isBlockquote ? "formattingActive" : "formattingInactive"}
            size="formatting"
            className={TOOLBAR_BUTTON_CLASS}
            onClick={onToggleBlockquote}
            type="button"
            aria-label={t("common.blockquote")}
          >
            &raquo;
          </Button>
        </ToolbarTooltip>

        {emojiPicker}

        {/* File Attach Button */}
        {showAttachButton && onAttachClick && (
          <>
            <ToolbarTooltip label={t("common.attach")}>
              <Button
                variant="formattingInactive"
                size="formatting"
                className={TOOLBAR_BUTTON_CLASS}
                onClick={onAttachClick}
                type="button"
                aria-label={t("common.attach")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773c0 1.796-1.498 3.227-3.313 3.227H4.781C2.708 13 1 11.366 1 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zM8 10.5a.5.5 0 0 0 .5-.5V6.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L7.5 6.707V10a.5.5 0 0 0 .5.5z" />
                </svg>
              </Button>
            </ToolbarTooltip>
            {fileInputRef && onFileChange && (
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileChange}
                className="hidden"
                multiple
              />
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
